import React, { useState, useEffect } from 'react'
import { dbOperations } from '../../utils/supabase'
import {
  ClipboardDocumentListIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserIcon,
  CalendarIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline'

const OrdersPage = () => {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [expandedOrders, setExpandedOrders] = useState(new Set())

  const statusOptions = [
    { value: 'pending', label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'processing', label: 'İşleniyor', color: 'bg-blue-100 text-blue-800' },
    { value: 'shipped', label: 'Kargoda', color: 'bg-purple-100 text-purple-800' },
    { value: 'delivered', label: 'Teslim Edildi', color: 'bg-green-100 text-green-800' },
    { value: 'cancelled', label: 'İptal Edildi', color: 'bg-red-100 text-red-800' }
  ]

  useEffect(() => {
    loadOrders()
  }, [])

  const loadOrders = async () => {
    try {
      setLoading(true)
      const { data } = await dbOperations.orders.getAll()
      setOrders(data || [])
    } catch (error) {
      console.error('Error loading orders:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const { data } = await dbOperations.orders.updateStatus(orderId, newStatus)
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ))
    } catch (error) {
      console.error('Error updating order status:', error)
    }
  }

  const toggleOrderExpansion = (orderId) => {
    const newExpanded = new Set(expandedOrders)
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId)
    } else {
      newExpanded.add(orderId)
    }
    setExpandedOrders(newExpanded)
  }

  const getStatusInfo = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0]
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
              order.user_profiles?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.user_profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = !filterStatus || order.status === filterStatus
    
    return matchesSearch && matchesStatus
  })

  const getTotalOrdersCount = () => orders.length
  const getTotalRevenue = () => orders.reduce((sum, order) => sum + parseFloat(order.total_amount), 0)
  const getPendingOrdersCount = () => orders.filter(order => order.status === 'pending').length

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Başlık ve İstatistikler */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Siparişler</h1>
        <p className="mt-2 text-sm text-gray-700">
          Tüm siparişleri görüntüleyin ve durumlarını güncelleyin.
        </p>
        
        {/* Özet İstatistikler */}
        <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClipboardDocumentListIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Toplam Sipariş
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {getTotalOrdersCount()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CurrencyDollarIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Toplam Gelir
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(getTotalRevenue())}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CalendarIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Bekleyen Siparişler
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {getPendingOrdersCount()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtreler */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4 sm:space-y-0 sm:flex sm:gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Sipariş ID, müşteri adı veya e-posta ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Tüm Durumlar</option>
            {statusOptions.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Sipariş Listesi */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Sipariş bulunamadı</h3>
            <p className="mt-1 text-sm text-gray-500">
              Arama kriterlerinize uygun sipariş bulunmuyor.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {filteredOrders.map((order) => {
              const statusInfo = getStatusInfo(order.status)
              const isExpanded = expandedOrders.has(order.id)
              
              return (
                <li key={order.id}>
                  <div className="px-4 py-4">
                    {/* Sipariş Başlığı */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <UserIcon className="h-10 w-10 text-gray-400" />
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {order.user_profiles?.full_name || order.user_profiles?.email || 'Bilinmeyen Kullanıcı'}
                            </p>
                            <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.color}`}>
                              {statusInfo.label}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span>Sipariş #{order.id.slice(0, 8)}</span>
                            <span>•</span>
                            <span>{formatDate(order.created_at)}</span>
                            <span>•</span>
                            <span className="font-medium text-gray-900">
                              {formatCurrency(order.total_amount)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <select
                          value={order.status}
                          onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                          className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          {statusOptions.map(status => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                        
                        <button
                          onClick={() => toggleOrderExpansion(order.id)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                        >
                          {isExpanded ? (
                            <ChevronUpIcon className="h-5 w-5" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Sipariş Detayları */}
                    {isExpanded && order.order_items && (
                      <div className="mt-4 pl-14">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Sipariş Ürünleri</h4>
                          <div className="space-y-2">
                            {order.order_items.map((item, index) => (
                              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0">
                                <div className="flex-1">
                                  <p className="text-sm text-gray-900">{item.products?.name || 'Ürün bulunamadı'}</p>
                                  <p className="text-xs text-gray-500">Adet: {item.quantity}</p>
                                </div>
                                <div className="text-sm font-medium text-gray-900">
                                  {formatCurrency(item.price * item.quantity)}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {order.shipping_address && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Teslimat Adresi</h4>
                              <div className="text-sm text-gray-600">
                                {order.shipping_address.address && <p>{order.shipping_address.address}</p>}
                                {order.shipping_address.city && order.shipping_address.district && (
                                  <p>{order.shipping_address.district}, {order.shipping_address.city}</p>
                                )}
                                {order.shipping_address.postalCode && <p>{order.shipping_address.postalCode}</p>}
                              </div>
                            </div>
                          )}
                          
                          {order.notes && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Notlar</h4>
                              <p className="text-sm text-gray-600">{order.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

export default OrdersPage 